import { IconLayer, ScatterplotLayer } from "@deck.gl/layers";
import { scaleOrdinal } from "d3-scale";
import React, { Component, createRef } from "react";

import DeckGL from "@deck.gl/react";
import ReactMapGL, {
  FlyToInterpolator,
  GeolocateControl,
  Marker,
  NavigationControl,
  Popup,
} from "react-map-gl";

import { withTheme } from "@mui/styles";
import {
  calculateMaxMi,
  DEFAULT_MAP_ZOOM,
  getInitialCoordinates,
  getStoreName,
  hexToRgb,
  MAX_ZOOM_GPS_LOCATION,
  MAX_ZOOM_HIGHLIGHTED_ICON,
  ShowMessage,
} from "../../Utils/Utils";

import { WebMercatorViewport } from "@deck.gl/core";
import { Close, LocationOn } from "@mui/icons-material";
import { IconButton, Paper, SwipeableDrawer } from "@mui/material";
import { grey } from "@mui/material/colors";
import { Box } from "@mui/system";
import _ from "lodash";
import TagManager from "react-gtm-module";
import { AutoSizer } from "react-virtualized";
import UserContext from "../../Contexts/UserContext";
import {
  doGetProducts,
  getStatesFromBounds,
  getStoreLocatorInventory,
  getZipcodesInRadius,
} from "../../Services/Data.service";
import PDP from "../Search/PDP";
import ResultDetails from "../Search/ResultDetails";
import SearchPanel from "../Search/SearchPanel";
import { getIsPdp } from "../../Utils/pdpUtils";

const MAPBOX_TOKEN =
  "pk.eyJ1IjoiZGF0aGljIiwiYSI6ImNrYzg2cHBmMTE0ODQycXQ2dHEzdmZlajEifQ.OZvk_-izmIuFlYmkq7kRIw";
const DEFAULT_MAP_STYLE = "mapbox://styles/dathic/ckc8kk348173w1iozrqb1jvmm";
const DARK_MAP_STYLE = "mapbox://styles/dathic/clxnsusm302r601qoflhjdr9w";

export const PUERTO_RICO_POS = { lat: 18.224587, lng: -66.423609 };
export const US_POS = { lat: 37.09024, lng: -95.712891 };
export const CA_POS = { lat: 29.617821, lng: -81.70622 };

class Map extends Component {
  static contextType = UserContext;
  layerKey = "";

  constructor(props) {
    super(props);
    const {
      mapContext,
      storeLocatorConfig,
      orgProperties,
      region,
    } = this.props;
    const params = new URLSearchParams(window.location.search);

    this.mapRef = createRef();
    const initialCoords = getInitialCoordinates(
      storeLocatorConfig,
      orgProperties,
      undefined,
      region,
    );
    const INITIAL_LATITIUDE = initialCoords.lat;
    const INITIAL_LONGITUDE = initialCoords.lng;
    const INITIAL_ZOOM = storeLocatorConfig?.initialZoom || DEFAULT_MAP_ZOOM;

    mapContext.setMapContext({
      mapStyle:
        storeLocatorConfig?.mapTheme === "dark"
          ? DARK_MAP_STYLE
          : DEFAULT_MAP_STYLE,
      results: [],
      highlight_result: { data: null },
      inTransition: false,
      isDragging: false,
      isZooming: false,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
        latitude: region === "pr" ? PUERTO_RICO_POS.lat : INITIAL_LATITIUDE,
        longitude: region === "pr" ? PUERTO_RICO_POS.lng : INITIAL_LONGITUDE,
        zoom: INITIAL_ZOOM,
        bearing: 0,
        pitch: 0,
        transitionDuration: 1000,
        transitionInterpolator: new FlyToInterpolator(),
        trackUserLocation: false,
      },
      toggleModalPresent: this.toggleModalPresent,
      updateViewport: this.updateViewport,
      setHoverResultItem: this.setHoverResultItem.bind(this),
      deliveryInventory: null,
      products: undefined,
      searchFilters: params.get("filters")
        ? JSON.parse(params.get("filters"))
        : { chain: "", type: "", product: [] },
      hoverInfo: undefined,
      hoverResultItem: undefined,
      layerUpdates: 0,
    });
  }

  componentDidMount() {
    doGetProducts().then((response) => {
      this.props.mapContext.setMapContext((oldState) => ({
        ...oldState,
        products: response || [],
      }));
    });
  }

  updateViewport = (viewport) => {
    this.props.mapContext.setMapContext((oldState) => ({
      ...oldState,
      viewport: viewport,
    }));
  };

  toggleModalPresent = (data, reviews, index, openInfo, filters) => {
    if (
      data &&
      Number.isFinite(data.latitude) &&
      Number.isFinite(data.longitude)
    ) {
      this.highlightResultOnMap(data);

      this.props.mapContext.setMapContext((oldState) => ({
        ...oldState,
        highlight_result: { data: data, reviews, index, openInfo, filters },
      }));
      this.fetchDelivery([data.latitude, data.longitude]);
    } else {
      this.props.mapContext.setMapContext((oldState) => ({
        ...oldState,
        highlight_result: { data: null, filters },
      }));
    }
  };

  highlightResultOnMap = (data) => {
    let viewport = this.props.mapContext.viewport;
    viewport.latitude = data.latitude;
    viewport.longitude = data.longitude;
    viewport.zoom = Math.max(
      MAX_ZOOM_HIGHLIGHTED_ICON,
      this.props.mapContext.viewport.zoom,
    );
    viewport.transitionDuration = 1000;
    this.props.mapContext.setMapContext((oldState) => ({
      ...oldState,
      viewport: viewport,
    }));
  };

  fetchDelivery = async (_coordinates) => {
    const map = this.mapRef.current?.getMap();
    const { viewport } = this.props.mapContext;
    const { accessToken } = this.context.state || {};
    const latitude = _coordinates?.[0] || viewport?.latitude;
    const longitude = _coordinates?.[1] || viewport?.longitude;
    if (latitude && longitude && viewport?.zoom && accessToken) {
      try {
        const defaultZipRadiusMi = this.props.storeLocatorConfig
          ?.deliveryZipcodeRadiusMi;
        const uniqueZipcodes = await getZipcodesInRadius(
          latitude,
          longitude,
          Math.min(
            calculateMaxMi(latitude, viewport?.zoom),
            defaultZipRadiusMi || 5,
          ),
        );
        let extraInventory;
        if (uniqueZipcodes?.length) {
          this.props.mapContext.setMapContext((oldState) => ({
            ...oldState,
            deliveryInventory: null,
          }));
          const organizationId = window.DATHIC_ORG_ID;
          const org_id = organizationId || process.env.REACT_APP_ORG_ID;
          extraInventory = await getStoreLocatorInventory(
            undefined,
            uniqueZipcodes,
            [],
            org_id,
            [
              "id_auto_delivery_inventory",
              "is_product_available",
              "delivery_product_uid",
              "buy_product_store_url",
              "source",
              "delivery_entity_uid",
              "delivery_product.uid",
              "delivery_product.source",
              "delivery_product.buy_url_product",
              "delivery_entity.store_url",
              "delivery_entity.source",
              "delivery_entity.phone",
            ],
          );
        }
        let visibleStatesOnMap;
        if (map) {
          const bounds = map.getBounds();
          visibleStatesOnMap = await getStatesFromBounds(
            bounds.getWest(),
            bounds.getSouth(),
            bounds.getEast(),
            bounds.getNorth(),
          );
        }
        this.props.mapContext.setMapContext((oldState) => ({
          ...oldState,
          visibleStatesOnMap: visibleStatesOnMap?.states,
          deliveryInventory: [...(extraInventory || [])].reduce(
            (acc, i) =>
              acc.find(
                (item) =>
                  item.id_auto_delivery_inventory ===
                  i.id_auto_delivery_inventory,
              )
                ? acc
                : [...acc, i],
            [],
          ),
        }));
      } catch (error) {
        this.props.mapContext.setMapContext((oldState) => ({
          ...oldState,
          deliveryInventory: [],
        }));
      }
    } else {
      this.props.mapContext.setMapContext((oldState) => ({
        ...oldState,
        deliveryInventory: [],
      }));
    }
  };

  addLayer = (data, shouldFlyToCoordinates, geopoliticalRegion) => {
    this.props.mapContext.setMapContext((prevState) => {
      let viewport = { ...prevState.viewport };

      if (
        shouldFlyToCoordinates &&
        Number.isFinite(shouldFlyToCoordinates.lat) &&
        Number.isFinite(shouldFlyToCoordinates.lng)
      ) {
        viewport.latitude = shouldFlyToCoordinates.lat;
        viewport.longitude = shouldFlyToCoordinates.lng;

        let shouldFitBounds = false;
        switch (geopoliticalRegion) {
          case "street_address":
            viewport.zoom = DEFAULT_MAP_ZOOM + 4;
            break;
          case "locality":
            viewport.zoom = DEFAULT_MAP_ZOOM + 2;
            break;
          case "postal_code":
            viewport.zoom = DEFAULT_MAP_ZOOM + 4;
            break;
          case "administrative_area_level_1":
            viewport.zoom = DEFAULT_MAP_ZOOM - 3;
            break;
          case "country":
            viewport.zoom = DEFAULT_MAP_ZOOM - 6;
            break;
          default:
            shouldFitBounds = true;
            break;
        }

        if (shouldFitBounds) {
          let storesToFitInBounds = _.map(_.first(_.chunk(data, 10)), (d) => [
            d.longitude,
            d.latitude,
          ]).filter(
            ([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat),
          );
          storesToFitInBounds.push([
            shouldFlyToCoordinates.lng,
            shouldFlyToCoordinates.lat,
          ]);

          if (storesToFitInBounds.length === 1) {
            storesToFitInBounds.push([
              shouldFlyToCoordinates.lng + 0.3,
              shouldFlyToCoordinates.lat + 0.3,
            ]);
          }
          if (viewport.width > 0 && viewport.height > 0) {
            try {
              const bounds = new WebMercatorViewport(viewport).fitBounds(
                storesToFitInBounds,
                {
                  padding: 20,
                  offset: [200, 0],
                },
              );

              viewport.latitude = bounds.latitude;
              viewport.longitude = bounds.longitude;
              viewport.zoom = bounds.zoom - 0.2;
            } catch (error) {
              // Leave viewport at the already-validated fly-to coordinates.
            }
          }
        }
      }

      return {
        ...prevState,
        results: data,
        viewport: viewport,
        layerUpdates: prevState.layerUpdates + 1,
      };
    });
  };

  setHoverResultItem(item) {
    this.props.mapContext.setMapContext((oldState) => ({
      ...oldState,
      hoverResultItem: item,
    }));
  }

  render() {
    const {
      theme,
      region,
      storeLocatorConfig,
      orgProperties,
      mapContext,
    } = this.props;
    const {
      highlight_result,
      trackUserLocation,
      viewport,
      results,
      hoverInfo,
      hoverResultItem,
      mapStyle,
    } = mapContext;
    const isPdp = getIsPdp();
    const isMobile = window.matchMedia("(max-width: 768px)").matches && !isPdp;

    const geolocateControl = (
      <GeolocateControl
        trackUserLocation={true}
        onViewportChange={(e) => {
          if (!Number.isFinite(e.latitude) || !Number.isFinite(e.longitude)) {
            return;
          }
          e.zoom = MAX_ZOOM_GPS_LOCATION;
          if (trackUserLocation) {
            TagManager.dataLayer({
              dataLayer: {
                event: "searchChange",
                category: "Search",
                action:
                  "Using user location: lat" +
                  e.latitude +
                  " lng: " +
                  e.longitude,
              },
            });
            this.props.mapContext.setMapContext((oldState) => ({
              ...oldState,
              viewport: e,
            }));
          }
          this.props.mapContext.setMapContext((oldState) => ({
            ...oldState,
            trackUserLocation: false,
          }));
        }}
      />
    );

    const getFillColor = (x) => {
      const colorBasedOnAttribute =
        storeLocatorConfig.mapPins?.type === "basedon" &&
        storeLocatorConfig.mapPins?.basedon_color?.colors?.length;
      let colorScale;
      if (colorBasedOnAttribute) {
        const basedOnValues =
          this.context.state?.properties?.find(
            (properties) =>
              properties.name === storeLocatorConfig.mapPins.value,
          )?.value || [];
        colorScale = scaleOrdinal()
          .domain(basedOnValues)
          .range(storeLocatorConfig.mapPins.basedon_color.colors);
      }
      if (colorBasedOnAttribute) {
        return hexToRgb(colorScale(x[storeLocatorConfig.mapPins.value]));
      }
      return hexToRgb(storeLocatorConfig.mapPins?.value || "#234587");
    };

    return (
      <React.Fragment>
        {isPdp && (
          <PDP
            addLayer={this.addLayer}
            savePointsToLayer={this.savePointsToLayer}
            onFilterChange={(searchFilters) => {
              this.props.mapContext.setMapContext((oldState) => ({
                ...oldState,
                searchFilters,
              }));
            }}
            setShowSearch={(showSearch) => {
              this.props.mapContext.setMapContext((oldState) => ({
                ...oldState,
                showSearch,
              }));
            }}
            fetchDelivery={this.fetchDelivery}
            region={region}
          />
        )}
        {!isPdp && (
          <SearchPanel
            addLayer={this.addLayer}
            savePointsToLayer={this.savePointsToLayer}
            onFilterChange={(searchFilters) => {
              this.props.mapContext.setMapContext((oldState) => ({
                ...oldState,
                searchFilters,
              }));
            }}
            setShowSearch={(showSearch) => {
              this.props.mapContext.setMapContext((oldState) => ({
                ...oldState,
                showSearch,
              }));
            }}
            fetchDelivery={this.fetchDelivery}
            region={region}
          />
        )}

        {!isPdp && (
          <AutoSizer style={{ height: "100%", width: "100%" }}>
            {({ width, height }) => (
              <ReactMapGL
                mapboxApiAccessToken={MAPBOX_TOKEN}
                {...viewport}
                width={width}
                ref={this.mapRef}
                height={height}
                onViewportChange={(viewport) => this.updateViewport(viewport)}
                mapStyle={mapStyle}
                attributionControl={false}
                onMouseDown={() =>
                  this.props.mapContext.setMapContext((oldState) => ({
                    ...oldState,
                    showSearch: false,
                  }))
                }
                onInteractionStateChange={(e) => {
                  this.props.mapContext.setMapContext((oldState) => ({
                    ...oldState,
                    inTransition: e.inTransition,
                    isDragging: e.isDragging,
                    isZooming: e.isZooming,
                  }));
                  setTimeout(() => {
                    this.props.mapContext.setMapContext((oldState) => ({
                      ...oldState,
                      inTransition: false,
                      isDragging: false,
                      isZooming: false,
                    }));
                  }, 2000);
                  if (!e.inTransition && !e.isDragging && !e.isZooming) {
                    this.updateViewport({
                      ...viewport,
                      transitionDuration: 1000,
                    });
                  }
                }}
              >
                <DeckGL
                  initialViewState={viewport}
                  getCursor={({ isHovering }) =>
                    isHovering ? "pointer" : "grab"
                  }
                >
                  {!!hoverResultItem && (
                    <Marker
                      longitude={hoverResultItem.longitude}
                      latitude={hoverResultItem.latitude}
                      offsetLeft={-15}
                      offsetTop={-30}
                    >
                      <LocationOn
                        style={{
                          fontSize: 30,
                          color:
                            storeLocatorConfig.mapHighlightPinColor || "#000",
                        }}
                      />
                    </Marker>
                  )}
                  {!!hoverInfo?.object && (
                    <Popup
                      longitude={hoverInfo.object.longitude}
                      latitude={hoverInfo.object.latitude}
                      anchor="top-left"
                      closeOnClick={false}
                      style={{ borderRadius: theme.shape.borderRadius }}
                      closeButton={false}
                    >
                      {getStoreName(
                        hoverInfo.object,
                        storeLocatorConfig,
                        orgProperties?.noChainNameReplacement,
                      )}
                    </Popup>
                  )}
                  {storeLocatorConfig.mapPins?.type === "icon" ? (
                    <IconLayer
                      onClick={(e) => {
                        setTimeout(() => {
                          ShowMessage(
                            e.object,
                            undefined,
                            true,
                            this.toggleModalPresent,
                            e.index,
                            this.props.mapContext.searchFilters,
                          );
                        }, 100);
                      }}
                      onHover={(hoverInfo) => {
                        this.props.mapContext.setMapContext((oldState) => ({
                          ...oldState,
                          hoverInfo,
                        }));
                      }}
                      key={`icon-layer`}
                      id={`icon-layer`}
                      data={results}
                      getIcon={() => ({
                        url: storeLocatorConfig.mapPins.value,
                        width: 128,
                        height: 128,
                        anchorY: 128,
                      })}
                      pickable={true}
                      dataComparator={(a, b) => {
                        const newKey = JSON.stringify(a?.map((x) => x.id));
                        const dataChanged = this.layerKey !== newKey;
                        this.layerKey = newKey;
                        return !dataChanged;
                      }}
                      getPosition={(x) => {
                        return [x.longitude, x.latitude];
                      }}
                      getSize={() => storeLocatorConfig.mapPins.size || 20}
                      sizeScale={1}
                      getColor={() => hexToRgb("#234587")}
                    />
                  ) : (
                    <ScatterplotLayer
                      onClick={(e) => {
                        ShowMessage(
                          e.object,
                          undefined,
                          true,
                          this.toggleModalPresent,
                          e.index,
                          this.props.mapContext.searchFilters,
                        );
                      }}
                      onHover={(hoverInfo) => {
                        this.props.mapContext.setMapContext((oldState) => ({
                          ...oldState,
                          hoverInfo,
                        }));
                      }}
                      key={`scatterplot-layer`}
                      id={`scatterplot-layer`}
                      data={results}
                      pickable
                      dataComparator={(a, b) => {
                        const newKey = JSON.stringify(a?.map((x) => x.id));
                        const dataChanged = this.layerKey !== newKey;
                        this.layerKey = newKey;
                        return !dataChanged;
                      }}
                      opacity={0.8}
                      stroked
                      filled
                      radiusMinPixels={7}
                      radiusMaxPixels={7}
                      getPosition={(x) => [x.longitude, x.latitude]}
                      getFillColor={getFillColor()}
                      getLineColor={[0, 0, 0, 1]}
                    />
                  )}
                </DeckGL>

                {!isMobile && (
                  <div
                    style={{
                      width: "30px",
                      float: "right",
                      margin: "24px 10px",
                    }}
                  >
                    <NavigationControl
                      onViewportChange={(v) => this.updateViewport(v)}
                    />
                  </div>
                )}

                {highlight_result?.data && !isMobile && (
                  <Popup
                    longitude={highlight_result.data.longitude}
                    latitude={highlight_result.data.latitude}
                    anchor="top-left"
                    closeOnClick={false}
                    style={{
                      borderRadius: theme.shape.borderRadius,
                    }}
                    onClose={() => this.toggleModalPresent()}
                  >
                    <div
                      ref={(el) =>
                        el?.addEventListener("wheel", (e) => {
                          e.stopPropagation();
                        })
                      }
                      style={{
                        width: 400,
                        maxHeight: "90vh",
                        overflowY: "auto",
                        position: "relative",
                        padding: 0,
                      }}
                    >
                      <ResultDetails
                        filters={highlight_result?.filters}
                        result={highlight_result?.data}
                        storeLocatorConfig={storeLocatorConfig}
                        orgProperties={orgProperties}
                        context={this.context}
                        reviews={highlight_result?.reviews}
                        openInfo={highlight_result?.openInfo}
                      />
                    </div>
                  </Popup>
                )}

                {!isPdp && (
                  <div
                    style={{
                      position: "absolute",
                      top: "51px",
                      ...(isMobile
                        ? { right: "70px", zIndex: 9 }
                        : { left: "340px" }),
                    }}
                    onClick={() => {
                      this.props.mapContext.setMapContext((oldState) => ({
                        ...oldState,
                        trackUserLocation: true,
                      }));
                    }}
                  >
                    {geolocateControl}
                  </div>
                )}
              </ReactMapGL>
            )}
          </AutoSizer>
        )}

        {highlight_result?.data && (isMobile || isPdp) && (
          <SwipeableDrawer
            anchor="bottom"
            open={!!highlight_result?.data}
            style={{ backgroundColor: "transparent" }}
            sx={{
              "& .MuiDrawer-paper": {
                borderTopLeftRadius: "16px",
                borderTopRightRadius: "16px",
                marginX: "0.6rem",
              },
            }}
            onClose={() => this.toggleModalPresent()}
            onOpen={() => {}}
          >
            <Paper style={{ overflow: "hidden", position: "relative" }}>
              <Box
                width="100%"
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
              >
                <Box></Box>
                <Box
                  width={30}
                  height={6}
                  backgroundColor={grey[400]}
                  borderRadius={3}
                  marginTop="8px"
                ></Box>
                <IconButton
                  aria-label="close-detail"
                  style={{
                    alignSelf: "flex-end",
                    fontWeight: "bold",
                    padding: 0,
                    marginTop: "8px",
                    marginRight: "8px",
                  }}
                  onClick={() => this.toggleModalPresent()}
                  size="medium"
                >
                  <Close fontSize="inherit" />
                </IconButton>
              </Box>
              <ResultDetails
                style={{ height: "50px" }}
                result={highlight_result?.data}
                storeLocatorConfig={storeLocatorConfig}
                orgProperties={orgProperties}
                context={this.context}
                reviews={highlight_result?.reviews}
                openInfo={highlight_result?.openInfo}
                filters={highlight_result?.filters}
              />
            </Paper>
          </SwipeableDrawer>
        )}
      </React.Fragment>
    );
  }
}
Map.propTypes = {};
export default withTheme(Map);
